/**
 * The human check before a shift (Cloudflare Turnstile), on the app's side.
 *
 * The server keeps the record that counts: an ad for a shift opens only for
 * someone who passed the check within the panel's window (two minutes by
 * default), and only the server, asking Cloudflare itself, writes that pass.
 * All the app does is draw the widget, hand its one-time answer to the server,
 * and remember until when the pass holds, so a second miner started within the
 * window does not show the widget again for nothing.
 */

/** The options of `turnstile.render` this app uses (Cloudflare's names). */
export interface TurnstileRenderParams {
  sitekey: string;
  /** Returned by Cloudflare with the answer; tells this widget's answers apart in its analytics. */
  action?: string;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'flexible' | 'compact';
  language?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  retry?: 'auto' | 'never';
  'refresh-expired'?: 'auto' | 'manual' | 'never';
  'refresh-timeout'?: 'auto' | 'manual' | 'never';
  'response-field'?: boolean;
  callback?: (token: string) => void;
  /** Returning true tells Turnstile the error was handled here. */
  'error-callback'?: (code: string) => boolean | void;
  'expired-callback'?: (token: string) => void;
  'timeout-callback'?: () => void;
  'unsupported-callback'?: () => void;
}

export interface TurnstileApi {
  /** The widget's id, or undefined when it could not be drawn. */
  render(container: HTMLElement, params: TurnstileRenderParams): string | null | undefined;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __pxcTurnstileReady?: () => void;
  }
}

/**
 * Cloudflare's script, from the exact address Cloudflare requires (it may not
 * be proxied or cached), drawn by hand rather than on page load.
 */
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__pxcTurnstileReady';

let loading: Promise<TurnstileApi> | null = null;

/**
 * Turnstile's script, once per page: loaded the first time the widget is
 * needed rather than at boot, so nobody downloads it before their first shift.
 */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    window.__pxcTurnstileReady = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile did not start'));
    };
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('Turnstile could not be loaded'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Until when (this phone's clock) the last pass holds; 0 for none. */
let passUntil = 0;

export const humanPass = {
  /** Whether the pass still holds for at least this many milliseconds. */
  validFor(ms: number): boolean {
    return passUntil - Date.now() > ms;
  },
  /** The server's word, read against this phone's clock through the server's own. */
  set(validUntil: number, serverTime: number): void {
    passUntil = Date.now() + Math.max(0, validUntil - serverTime);
  },
  clear(): void {
    passUntil = 0;
  },
};
